import React from 'react';
import { ConfirmDialog } from '@components/dialogs/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useDeletePortalScoreSubmissionMutation } from '@reactQuery/competitions';
import { enqueueSnackbar } from 'notistack';
import { translateApiValidationError } from '@api/helpers';
import { AxiosError } from 'axios';
import { ValidationErrors } from '@api/interfaces';
import { IDeletePendingScoreSubmissionDialog } from './interfaces';

export const DeletePendingScoreSubmissionDialog: React.FC<
  IDeletePendingScoreSubmissionDialog
> = ({ show, slug, submission, onClose, onDeleted }): JSX.Element => {
  const { t } = useTranslation();
  const { mutate: deleteSubmission, isPending } =
    useDeletePortalScoreSubmissionMutation();

  const handleConfirm = (): void => {
    if (!submission || isPending) {
      return;
    }

    deleteSubmission(
      { slug, submissionId: submission.id },
      {
        onSuccess: () => {
          onDeleted();
          enqueueSnackbar(
            t('competitions:portal.yourScores.delete.messages.success'),
            { variant: 'success' }
          );
        },
        onError: (error: AxiosError<ValidationErrors>) => {
          const code = error.response?.data?.errors?.submission?.[0];
          const message =
            translateApiValidationError(code) ??
            t('competitions:portal.yourScores.delete.messages.failed');

          enqueueSnackbar(message, { variant: 'error' });
        },
      }
    );
  };

  return (
    <ConfirmDialog
      show={show}
      title={t('competitions:portal.yourScores.delete.title')}
      subTitle={t('competitions:portal.yourScores.delete.subTitle')}
      onClose={() => {
        if (!isPending) {
          onClose();
        }
      }}
      onConfirm={handleConfirm}
      confirmLabel={t('competitions:portal.yourScores.delete.buttons.confirm')}
      confirmVariant="danger"
      disabled={isPending}
    >
      {t('competitions:portal.yourScores.delete.body')}
    </ConfirmDialog>
  );
};
